import {
    IQueryContext,
    IQueryContextDatasourceColumn,
} from 'src/app/services/query-context/models/IQueryContext';
import {
    ICompiledSQLTokens,
    IDatasourceSQLMap,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import { DatePickerSqlTokensCompiler } from 'src/app/utils/datasource-sql-map/date-picker-sql-tokens-compiler';
import { multipleSelectComponentCompileSQLTokens } from 'src/app/utils/datasource-sql-map/utils';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { buildSplitGroupSQL, buildWhereSQL } from 'src/app/utils/query/sql';
import { sqlDateTrunc } from 'src/app/utils/sql';

export class DashboardGcpBillingSqlMap {
    public static sqlMap: IDatasourceSQLMap = {
        dataset: 'bi_viz',
        tableId: 'dashboard_gcp_billing',
        columns: {
            ACTIVITY_DATE: {
                columnId: 'ACTIVITY_DATE',
                columnSQL: 'DATE(date)',
                compileSQLTokens: (state) => {
                    return DatePickerSqlTokensCompiler.compileTokens(
                        'DATE(date)',
                        state
                    );
                },
            },
            CLOUD_PROJECT: {
                columnId: 'CLOUD_PROJECT',
                columnSQL: 'cloud_project',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'cloud_project',
                        state
                    );
                },
            },
            CLOUD_SERVICE: {
                columnId: 'CLOUD_SERVICE',
                columnSQL: 'cloud_service',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'cloud_service',
                        state
                    );
                },
            },
            CLOUD_SKU: {
                columnId: 'CLOUD_SKU',
                columnSQL: 'cloud_sku',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'cloud_sku',
                        state
                    );
                },
            },
            CLOUD_SKU_SUMMARY: {
                columnId: 'CLOUD_SKU_SUMMARY',
                columnSQL: 'cloud_sku_summary',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'cloud_sku_summary',
                        state
                    );
                },
            },
            BUSINESS_PROJECT: {
                columnId: 'BUSINESS_PROJECT',
                columnSQL: 'business_project',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'business_project',
                        state
                    );
                },
            },
            BUSINESS_SERVICE: {
                columnId: 'BUSINESS_SERVICE',
                columnSQL: 'business_service',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'business_service',
                        state
                    );
                },
            },
            BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_PROJECT: {
                columnId: 'BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_PROJECT',
                columnSQL: 'business_service_details.bijobs.job_id.project',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'business_service_details.bijobs.job_id.project',
                        state
                    );
                },
            },
            BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_CATEGORY: {
                columnId: 'BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_CATEGORY',
                columnSQL: 'business_service_details.bijobs.job_id.category',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'business_service_details.bijobs.job_id.category',
                        state
                    );
                },
            },
            BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_NAME: {
                columnId: 'BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_NAME',
                columnSQL: 'business_service_details.bijobs.job_id.name',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'business_service_details.bijobs.job_id.name',
                        state
                    );
                },
            },
            BUSINESS_SERVICE_DETAILS_BIJOBS_ORCHESTRATOR_OPTIONS_TYPE: {
                columnId:
                    'BUSINESS_SERVICE_DETAILS_BIJOBS_ORCHESTRATOR_OPTIONS_TYPE',
                columnSQL:
                    'business_service_details.bijobs.orchestrator_options.type',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'business_service_details.bijobs.orchestrator_options.type',
                        state
                    );
                },
            },
            BUSINESS_SERVICE_DETAILS_BIJOBS_ORCHESTRATOR_OPTIONS_PERIOD: {
                columnId:
                    'BUSINESS_SERVICE_DETAILS_BIJOBS_ORCHESTRATOR_OPTIONS_PERIOD',
                columnSQL:
                    'business_service_details.bijobs.orchestrator_options.period',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'business_service_details.bijobs.orchestrator_options.period',
                        state
                    );
                },
            },
            CLOUD_SERVICE_DETAILS_BIGQUERY_STORAGE_DATASET_REF_PROJECT_ID: {
                columnId:
                    'CLOUD_SERVICE_DETAILS_BIGQUERY_STORAGE_DATASET_REF_PROJECT_ID',
                columnSQL:
                    'cloud_service_details.bigquery.storage.dataset_ref.project_id',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'cloud_service_details.bigquery.storage.dataset_ref.project_id',
                        state
                    );
                },
            },
            CLOUD_SERVICE_DETAILS_BIGQUERY_STORAGE_DATASET_REF_DATASET_ID: {
                columnId:
                    'CLOUD_SERVICE_DETAILS_BIGQUERY_STORAGE_DATASET_REF_DATASET_ID',
                columnSQL:
                    'cloud_service_details.bigquery.storage.dataset_ref.dataset_id',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'cloud_service_details.bigquery.storage.dataset_ref.dataset_id',
                        state
                    );
                },
            },
        },
    };

    public static compileSQLStatements(
        supportedDatasourceColumns: IQueryContextDatasourceColumn[],
        queryContextSettings: IQueryContext['settings'],
        options: { gcpProjectId: GcpProjectId },
        customCompiledSqlTokens?: Partial<ICompiledSQLTokens>
    ) {
        const dateTrunc = queryContextSettings.dateAggregation
            ? sqlDateTrunc('DATE(date)', queryContextSettings.dateAggregation)
            : `date`;

        const sqlTokensArray: Partial<ICompiledSQLTokens>[] =
            supportedDatasourceColumns.map((dsCol) => {
                return this.sqlMap.columns[dsCol.id]!.compileSQLTokens!(
                    dsCol.state,
                    options
                );
            });
        // merge tokens into single deduplicated object of type ICompiledSQLTokens
        const sqlTokens: ICompiledSQLTokens =
            mergeSqlTokensArray(sqlTokensArray);

        const splitSelect: string = buildSplitGroupSQL(sqlTokens.split_group);
        const splitGroupBy = splitSelect ? 'split_group, ' : '';
        const where: string = buildWhereSQL([
            ...sqlTokens.where,
            ...(customCompiledSqlTokens?.where ?? []),
        ]);

        return {
            dateTrunc,
            splitSelect,
            splitGroupBy,
            where,
        };
    }
}
